/**
 * Reading an auth callback URL.
 *
 * Supabase delivers a callback in one of two shapes, and this app sees both:
 *
 *   ?code=...                       PKCE — the Google OAuth path, which this
 *                                   page initiates and so holds a verifier for.
 *   #access_token=...&type=signup   Implicit — the magic-link path. The send is
 *                                   initiated SERVER-side by `otp-request`, so
 *                                   there is no code verifier in this browser
 *                                   and PKCE is not available to it.
 *
 * ============================================================================
 *  BOTH `type=signup` AND `type=magiclink` are accepted. Never narrow this.
 * ============================================================================
 *
 * The callback arrives as `type=signup` on the FIRST click for a new
 * `auth.users` row — which is every rep, exactly once, on their first ever
 * sign-in. It arrives as `type=magiclink` every time after that.
 *
 * A handler that accepts only `magiclink` therefore fails for 100% of reps on
 * first sign-in and works perfectly forever after. During launch week that
 * presents as "the portal does not work" from every new rep and "works fine"
 * from everyone testing a second time — the worst possible shape of bug.
 *
 * The defence here is structural, not just a wider allowlist: the session is
 * established by the Supabase client reading the tokens, which is entirely
 * type-agnostic, and `type` is NEVER used to decide whether to accept a
 * session. KNOWN_CALLBACK_TYPES exists to document the two expected values and
 * to make an unexpected one visible in the console — it is not a gate. If you
 * are about to write `if (type !== 'magiclink') reject()`, that is the bug this
 * comment is here to stop.
 */

export const KNOWN_CALLBACK_TYPES = ['signup', 'magiclink'] as const

export type CallbackType = (typeof KNOWN_CALLBACK_TYPES)[number]

export interface CallbackParams {
  /** True if this URL looks like an auth callback at all. */
  isCallback: boolean
  /** PKCE authorization code, if present. */
  code: string | null
  /** Implicit-flow token present in the fragment. */
  hasImplicitToken: boolean
  /** The raw `type` value, whatever it was. Informational only. */
  type: string | null
  /** Provider or Supabase error, from either the query or the fragment. */
  error: string | null
  errorDescription: string | null
  /** Return destination carried on the OAuth redirect. */
  next: string | null
}

export function readCallbackParams(url: URL): CallbackParams {
  const query = url.searchParams
  // The fragment is `#a=b&c=d`; strip the leading '#' before parsing.
  const hash = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash)

  const code = query.get('code')
  const hasImplicitToken = hash.has('access_token')
  const type = hash.get('type') ?? query.get('type')
  const error = query.get('error') ?? hash.get('error')
  const errorDescription =
    query.get('error_description') ?? hash.get('error_description') ?? null

  return {
    isCallback: Boolean(code || hasImplicitToken || error),
    code,
    hasImplicitToken,
    type,
    error,
    errorDescription,
    next: query.get('next'),
  }
}

/** Informational. Deliberately NOT used to accept or refuse a session. */
export function isKnownCallbackType(type: string | null): type is CallbackType {
  return type !== null && (KNOWN_CALLBACK_TYPES as readonly string[]).includes(type)
}

/**
 * Remove auth material from the address bar without adding a history entry.
 *
 * Tokens in the fragment end up in browser history, in screenshots and in
 * anything a rep pastes into a support thread. Clearing them is hygiene, not
 * cosmetics. The session is already in storage by the time this runs.
 */
export function stripAuthParamsFromUrl(): void {
  const url = new URL(window.location.href)
  for (const key of ['code', 'next', 'type', 'error', 'error_description', 'error_code']) {
    url.searchParams.delete(key)
  }
  url.hash = ''
  window.history.replaceState({}, '', `${url.pathname}${url.search}`)
}
